import { useEffect, useRef } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

const SHOULD_FORCE_CACHE_CLEAR = gql`
  query ShouldForceCacheClear {
    shouldForceCacheClear
  }
`;

const CLEAR_CACHE_BUST_FLAG = gql`
  mutation ClearCacheBustFlag {
    clearCacheBustFlag
  }
`;

const CacheBustDetector = () => {
  const hasTriggeredRef = useRef(false);
  const { data: cacheBustData } = useQuery(SHOULD_FORCE_CACHE_CLEAR, {
    pollInterval: 10000, // Check every 10 seconds
    fetchPolicy: 'network-only' // Always check server
  });

  const [clearCacheBustFlag] = useMutation(CLEAR_CACHE_BUST_FLAG);

  useEffect(() => {
    if (!cacheBustData?.shouldForceCacheClear || hasTriggeredRef.current) {
      return;
    }

    hasTriggeredRef.current = true;
    console.log('Cache bust detected, forcing cache clear...');

    const runCacheClear = async () => {
      try {
        await clearCacheBustFlag();
      } catch (error) {
        console.error(error);
      }
      await forceCacheClear();
    };

    runCacheClear();
  }, [cacheBustData, clearCacheBustFlag]);

  const forceCacheClear = async () => {
    try {
      // PWA: Send message to service worker to force cache clear
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        try {
          navigator.serviceWorker.controller.postMessage({
            type: 'FORCE_CACHE_CLEAR'
          });
        } catch (error) {
          // Silent error handling
        }
      }

      // PWA: Unregister service worker to force fresh installation
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(registration => {
              return registration.unregister();
            })
          );
        } catch (error) {
          // Silent error handling
        }
      }

      // Clear all browser caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
      }

      // Clear local storage (except authentication)
      const authToken = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore auth tokens
      if (authToken) localStorage.setItem('authToken', authToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      // Force reload with cache busting
      const url = new URL(window.location.href);
      url.searchParams.set('_cacheBust', Date.now());
      url.searchParams.set('_forceReload', 'true');
      
      window.location.href = url.href;
    } catch (error) {
      window.location.reload(true);
    }
  };

  // This component doesn't render anything visible
  return null;
};

export default CacheBustDetector;
